import { DatePipe } from "@angular/common";
import { Component, OnInit, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { ngxEditorConfig } from "@config/angular-editor.config";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

/**
 * The shop's own house rules — ordering, packing, live arrival, collection —
 * shown on the merchant's storefront page behind a "Merchant Guidelines" link.
 *
 * One guideline per shop, not one per country like delivery information: a
 * delivery fee genuinely differs by destination, these do not.
 *
 * The API reads and writes only the logged-in merchant's row; the id is taken
 * from the session token and never from this page. Admins can edit the same row
 * from the vendor page, and the last edit wins — there is no second copy.
 */
@Component({
  selector: "fh-shop-guideline",
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, AngularEditorModule, DatePipe],
  templateUrl: "./shop-guideline.component.html",
})
export class ShopGuidelineComponent implements OnInit {
  readonly contentControl = new FormControl<string>("");

  editorConfig = {
    ...ngxEditorConfig,
    enableToolbar: true,
    showToolbar: true,
    // The same buttons SEO management hides: their output is stripped by the
    // storefront's sanitiser, so offering them would let a merchant style
    // something that silently arrives plain.
    toolbarHiddenButtons: [
      ["subscript", "superscript", "indent", "outdent", "fontName"],
      ["fontSize", "textColor", "backgroundColor", "customClasses", "insertVideo"],
    ],
  };

  loading = signal(true);
  /** Save only once the stored guideline has loaded: otherwise it would overwrite it with blank. */
  loaded = signal(false);
  saving = signal(false);
  success = signal("");
  error = signal("");
  updatedAt = signal<string | null>(null);

  constructor(private requestService: RequestService) {}

  ngOnInit(): void {
    this.requestService.get<IApiResponse>("/api/v2/shop-guideline").subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.status) {
          const data = res.data as { content: string; updatedAt: string | null };
          this.contentControl.setValue(data?.content ?? "");
          this.updatedAt.set(data?.updatedAt ?? null);
          this.loaded.set(true);
        } else {
          this.loadFailed(res.message);
        }
      },
      error: () => {
        this.loading.set(false);
        this.loadFailed();
      },
    });
  }

  private loadFailed(message?: string): void {
    this.contentControl.disable();
    this.error.set(
      message || "Could not load your merchant guideline. Refresh the page to try again."
    );
  }

  save(): void {
    if (this.saving() || !this.loaded()) return;
    this.saving.set(true);
    this.success.set("");
    this.error.set("");

    this.requestService
      .post<IApiResponse>("/api/v2/shop-guideline", {
        // An editor emptied by hand reports null, which would otherwise be
        // stored as the string "null".
        content: this.contentControl.value ?? "",
      })
      .subscribe({
        next: (res) => {
          this.saving.set(false);
          if (res.status) {
            this.success.set("Merchant guidelines saved");
            const data = res.data as { updatedAt: string | null } | undefined;
            if (data?.updatedAt) this.updatedAt.set(data.updatedAt);
          } else {
            this.error.set(res.message || "Could not save your merchant guideline");
          }
        },
        error: () => {
          this.saving.set(false);
          this.error.set("Could not save your merchant guideline");
        },
      });
  }
}
